import { computed, ref } from 'vue'
import { Room, RoomEvent, supportsAudioOutputSelection } from 'livekit-client'

import type { ComputedRef, Ref } from 'vue'

import { refRoom } from './room-live-kit.hook'

interface DeviceOption {
  deviceId: string
  label: string
}

type Unsubscribe = () => void

interface UseDevicesLiveKitReturn {
  cameras: Ref<DeviceOption[]>
  microphones: Ref<DeviceOption[]>
  speakers: Ref<DeviceOption[]>
  selectedCameraId: Ref<string>
  selectedMicId: Ref<string>
  selectedSpeakerId: Ref<string>
  hasPermissions: ComputedRef<boolean>
  loadDevices: () => Promise<void>
  subscribeDevices: () => Unsubscribe
  selectCamera: (deviceId: string) => Promise<void>
  selectMicrophone: (deviceId: string) => Promise<void>
  selectSpeaker: (deviceId: string) => Promise<void>
}

const toOptions = (list: MediaDeviceInfo[]): DeviceOption[] =>
  list
    .filter((device) => device.label !== '')
    .map((device) => ({
      deviceId: device.deviceId,
      label: device.label,
    }))

export const useDevicesLiveKit = (): UseDevicesLiveKitReturn => {
  const cameras = ref<DeviceOption[]>([])
  const microphones = ref<DeviceOption[]>([])
  const speakers = ref<DeviceOption[]>([])
  const selectedCameraId = ref<string>('')
  const selectedMicId = ref<string>('')
  const selectedSpeakerId = ref<string>('')

  const hasPermissions = computed<boolean>(() => cameras.value.length > 0 && microphones.value.length > 0)

  const loadDevices = async (): Promise<void> => {
    const room = refRoom.value

    cameras.value = toOptions(await Room.getLocalDevices('videoinput', false))
    microphones.value = toOptions(await Room.getLocalDevices('audioinput', false))
    // audiooutput selection is unsupported on Safari/iOS (setSinkId) — hide it there.
    speakers.value = supportsAudioOutputSelection() ? toOptions(await Room.getLocalDevices('audiooutput', false)) : []

    selectedCameraId.value = room?.getActiveDevice('videoinput') ?? ''
    selectedMicId.value = room?.getActiveDevice('audioinput') ?? ''
    selectedSpeakerId.value = room?.getActiveDevice('audiooutput') ?? ''
  }

  const subscribeDevices = (): Unsubscribe => {
    const room = refRoom.value
    if (!room) return () => {}

    room
      .on(RoomEvent.MediaDevicesChanged, loadDevices)
      .on(RoomEvent.ActiveDeviceChanged, loadDevices)

    return () => {
      room
        .off(RoomEvent.MediaDevicesChanged, loadDevices)
        .off(RoomEvent.ActiveDeviceChanged, loadDevices)
    }
  }

  const selectCamera = async (deviceId: string): Promise<void> => {
    const room = refRoom.value
    if (!room) return

    try {
      await room.switchActiveDevice('videoinput', deviceId)
      selectedCameraId.value = deviceId
    } catch (error) {
      console.error('[classroom] failed to switch camera', error)
    }
  }

  const selectMicrophone = async (deviceId: string): Promise<void> => {
    const room = refRoom.value
    if (!room) return

    try {
      await room.switchActiveDevice('audioinput', deviceId)
      selectedMicId.value = deviceId
    } catch (error) {
      console.error('[classroom] failed to switch microphone', error)
    }
  }

  const selectSpeaker = async (deviceId: string): Promise<void> => {
    const room = refRoom.value
    if (!room) return

    try {
      await room.switchActiveDevice('audiooutput', deviceId)
      selectedSpeakerId.value = deviceId
    } catch (error) {
      console.error('[classroom] failed to switch speaker', error)
    }
  }

  return {
    cameras,
    microphones,
    speakers,
    selectedCameraId,
    selectedMicId,
    selectedSpeakerId,
    hasPermissions,
    loadDevices,
    subscribeDevices,
    selectCamera,
    selectMicrophone,
    selectSpeaker,
  }
}
